/**
 * Composant pour gérer les affiliations des utilisateurs aux organisations
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { UserAffiliation, CreateUserAffiliationRequest, UpdateUserAffiliationRequest, ROLE_PERMISSIONS } from '@/models/userAffiliation';
import { useUserAffiliation } from '@/shared/hooks/useUserAffiliation';
import { useAuth } from '@/app/providers';
import { usePermissionsHook } from '@/shared/hooks/usePermissionsHook';
import { UserInfoRepository, UserInfo } from '@/services/firebase/user-info-repository';
import { CustomerService } from '@/services/firebase/customer-service';
import { CheckCircle, XCircle, Briefcase, Globe, Save, Users, Mail } from 'lucide-react';
import { OrgaConfigurationRepository } from '@/services/firebase/orga-configuration-repository';
import { OrgaConfiguration } from '@/models/orgaConfiguration';
import { Card, CardHeader, CardBody, Button } from '@/components/ui';

interface UserAffiliationManagerProps {
  organizationId: string;
}

const UserAffiliationManager: React.FC<UserAffiliationManagerProps> = ({ organizationId }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const {
    permissionsLoading,
    isAdminForOrganization,
    getUserRole,
    refreshPermissions,
    canManageUsers: checkCanManageUsers
  } = usePermissionsHook();
  const {
    affiliations,
    loading,
    createAffiliation,
    updateAffiliation,
    deleteAffiliation,
    getOrganizationUsers
  } = useUserAffiliation();

  const [organizationUsers, setOrganizationUsers] = useState<UserAffiliation[]>([]);
  const [usersInfo, setUsersInfo] = useState<Map<string, UserInfo>>(new Map());
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState<UserAffiliation['role']>('member');
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  // Recruitment State
  const [orgaConfig, setOrgaConfig] = useState<OrgaConfiguration | null>(null);
  const [recruitmentLang, setRecruitmentLang] = useState<'fr' | 'en' | 'es'>('fr');
  const [descriptionDraft, setDescriptionDraft] = useState<string>('');
  const [isSavingRecruitment, setIsSavingRecruitment] = useState(false);

  // Load Config
  useEffect(() => {
    if (!organizationId) return;
    const unsubscribe = OrgaConfigurationRepository.listenToOrganizationConfig(
      organizationId,
      (config) => {
        setOrgaConfig(config);
      },
    );
    return () => unsubscribe();
  }, [organizationId]);

  // Update draft when config or lang changes
  useEffect(() => {
    if (orgaConfig?.recruitment?.description) {
      setDescriptionDraft(orgaConfig.recruitment.description[recruitmentLang] || '');
    } else {
      setDescriptionDraft('');
    }
  }, [orgaConfig, recruitmentLang]);

  const handleRecruitmentToggle = async () => {
    if (!orgaConfig) return;
    const newState = !orgaConfig.recruitment?.enabled;

    try {
      await OrgaConfigurationRepository.update(organizationId, {
        recruitment: {
          description: orgaConfig.recruitment?.description || {},
          ...orgaConfig.recruitment, // Keep other fields if any
          enabled: newState,
        }
      });
      toast.success(newState ? t('admin.configuration.users.recruitment.enabled') : t('admin.configuration.users.recruitment.disabled'));
    } catch (error) {
      console.error('Error updating recruitment toggle:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDescriptionSave = async () => {
    if (!orgaConfig) return;
    setIsSavingRecruitment(true);
    try {
      const currentDesc = orgaConfig.recruitment?.description || {};
      await OrgaConfigurationRepository.update(organizationId, {
        recruitment: {
          enabled: orgaConfig.recruitment?.enabled || false,
          description: {
            ...currentDesc,
            [recruitmentLang]: descriptionDraft
          }
        }
      });
      toast.success(t('admin.configuration.users.recruitment.descriptionSaved'));
    } catch (error) {
      console.error('Error updating description:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setIsSavingRecruitment(false);
    }
  };

  // Vérifier si l'utilisateur actuel peut gérer les utilisateurs
  const canManageUsers = organizationId ? checkCanManageUsers(organizationId) : false;

  // Debug: Log des informations de permission
  React.useEffect(() => {
    console.log('🔐 UserAffiliationManager - Vérification des permissions:', {
      organizationId,
      currentUserId: currentUser?.uid,
      canManageUsers,
      permissionsLoading,
      userRole: getUserRole(organizationId),
      isAdminForOrganization: isAdminForOrganization(organizationId),
      affiliationsCount: affiliations.length,
      affiliations: affiliations.map(a => ({
        id: a.id,
        customerId: a.customerId,
        organizationId: a.organizationId,
        role: a.role,
        status: a.status
      }))
    });
  }, [organizationId, currentUser?.uid, canManageUsers, permissionsLoading, getUserRole, isAdminForOrganization, affiliations]);

  // Charger les informations utilisateur
  const loadUsersInfo = async (affiliations: UserAffiliation[]) => {
    try {
      // Extraire les customerIds des affiliations
      const customerIds = affiliations
        .map(a => a.customerId)
        .filter((customerId): customerId is string => customerId !== undefined);

      const usersInfoMap = await UserInfoRepository.getUsersInfoByCustomerIds(customerIds, organizationId);

      setUsersInfo(usersInfoMap);
    } catch (error) {
      console.error('❌ Erreur lors du chargement des informations utilisateur:', error);
    }
  };

  // Charger les utilisateurs de l'espace de vente
  useEffect(() => {
    loadOrganizationUsers();
  }, [organizationId, getOrganizationUsers]);

  const loadOrganizationUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const users = await getOrganizationUsers(organizationId);
      setOrganizationUsers(users);

      // Charger les informations utilisateur
      await loadUsersInfo(users);
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
      toast.error(t('admin.configuration.users.loadingUsersError'));
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUserEmail.trim()) return;

    try {
      // Trouver ou créer le customer par email (sans Firebase Auth pour les invitations)
      const customer = await CustomerService.findOrCreateCustomer(newUserEmail);
      console.log(`✅ Customer trouvé/créé pour invitation: ${customer.id}`);

      const request: CreateUserAffiliationRequest = {
        customerId: customer.id, // customerId est requis
        email: newUserEmail, // ✅ Enregistrer explicitement l'email
        organizationId: organizationId,
        roleName: newUserRole, // ✅ Utiliser roleName (référence au rôle)
        invitedBy: currentUser?.uid
      };

      await createAffiliation(request);
      toast.success(t('admin.configuration.users.success'));

      // Réinitialiser le formulaire
      setNewUserEmail('');
      setNewUserRole('member');
      setShowAddUserForm(false);

      // Recharger la liste et les permissions
      await loadOrganizationUsers();
      await refreshPermissions();

    } catch (error) {
      console.error('Erreur lors de l\'ajout de l\'utilisateur:', error);
      toast.error(t('admin.configuration.users.error'));
    }
  };

  const handleUpdateRole = async (affiliationId: string, newRole: UserAffiliation['role']) => {
    try {
      // Convertir le rôle en roleName (string)
      const roleName = typeof newRole === 'string' ? newRole : (newRole as any);
      const request: UpdateUserAffiliationRequest = {
        roleName: roleName
      };

      await updateAffiliation(affiliationId, request);
      toast.success(t('admin.configuration.users.updateRoleSuccess'));

      // Recharger la liste et les permissions
      await loadOrganizationUsers();
      await refreshPermissions();

    } catch (error) {
      console.error('Erreur lors de la mise à jour du rôle:', error);
      toast.error(t('admin.configuration.users.updateRoleError'));
    }
  };

  const handleRemoveUser = async (affiliationId: string, userName: string) => {
    if (!confirm(t('admin.configuration.users.removeUserConfirm', { name: userName }))) {
      return;
    }

    try {
      await deleteAffiliation(affiliationId);
      toast.success(t('admin.configuration.users.removeUserSuccess'));

      // Recharger la liste et les permissions
      await loadOrganizationUsers();
      await refreshPermissions();

    } catch (error) {
      console.error('Erreur lors de la suppression de l\'utilisateur:', error);
      toast.error(t('admin.configuration.users.removeUserError'));
    }
  };

  const handleToggleEmailNotifications = async (affiliationId: string, currentValue: boolean) => {
    try {
      const request: UpdateUserAffiliationRequest = {
        emailNotifications: {
          savMessages: !currentValue
        }
      } as any;

      await updateAffiliation(affiliationId, request);
      toast.success(!currentValue ? 'Notifications email activées' : 'Notifications email désactivées');

      // Recharger la liste
      await loadOrganizationUsers();

    } catch (error) {
      console.error('Erreur lors de la mise à jour des notifications:', error);
      toast.error('Erreur lors de la mise à jour des notifications');
    }
  };

  const getRoleBadgeColor = (role: UserAffiliation['role']) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-100 text-purple-800';
      case 'admin':
        return 'bg-primary-10 text-primary-dark';
      case 'member':
        return 'bg-success-10 text-success-dark';
      case 'viewer':
        return 'bg-background-tertiary text-foreground-secondary';
      default:
        return 'bg-background-tertiary text-foreground-secondary';
    }
  };

  const getRoleLabel = (role: UserAffiliation['role']) => {
    switch (role) {
      case 'owner':
        return t('admin.configuration.users.owner');
      case 'admin':
        return t('admin.configuration.users.admin');
      case 'member':
        return t('admin.configuration.users.member');
      case 'viewer':
        return t('admin.configuration.users.viewer');
      default:
        return role;
    }
  };

  // Obtenir les permissions pour un rôle donné
  const getRolePermissions = (role: UserAffiliation['role']) => {
    return ROLE_PERMISSIONS[role];
  };

  // Obtenir le label d'une permission
  const getPermissionLabel = (permission: keyof UserAffiliation['permissions']) => {
    const labels: Record<keyof UserAffiliation['permissions'], string> = {
      canViewOrders: t('admin.configuration.users.permissions.canViewOrders'),
      canManageOrders: t('admin.configuration.users.permissions.canManageOrders'),
      canManageConfiguration: t('admin.configuration.users.permissions.canManageConfiguration'),
      canViewAnalytics: t('admin.configuration.users.permissions.canViewAnalytics'),
      canManageUsers: t('admin.configuration.users.permissions.canManageUsers'),
    };
    return labels[permission];
  };

  if (!canManageUsers && !permissionsLoading) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-center">
          <svg className="w-5 h-5 text-yellow-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div>
            <p className="text-yellow-800 font-medium">
              {t('admin.configuration.users.insufficientPermissions')}
            </p>
            <p className="text-yellow-700 text-sm mt-1">
              {t('admin.configuration.users.noPermissionsDesc')}
            </p>
            <p className="text-yellow-600 text-xs mt-2">
              {t('admin.configuration.users.contactAdmin')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 min-h-[600px]">
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-foreground leading-tight">{t('admin.configuration.users.title')}</h2>
          <p className="text-xs sm:text-sm text-foreground-secondary mt-0.5 sm:mt-1">{t('admin.configuration.users.subtitle')}</p>
        </div>
      </div>

      <Card variant="default">
        <CardBody className="p-6">
          {/* Section Recrutement */}
          <div className="mb-8 bg-background-tertiary rounded-lg p-4 border border-border">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div className="flex items-center space-x-2">
                <Briefcase className="w-5 h-5 text-foreground-secondary flex-shrink-0" />
                <h4 className="text-md font-medium text-foreground">{t('admin.configuration.users.recruitment.enableTitle')}</h4>
              </div>

              {/* Switch */}
              <button
                type="button"
                className={`${orgaConfig?.recruitment?.enabled ? 'bg-primary' : 'bg-border-medium'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 self-start sm:self-auto`}
                role="switch"
                aria-checked={orgaConfig?.recruitment?.enabled}
                onClick={handleRecruitmentToggle}
              >
                <span className={`${orgaConfig?.recruitment?.enabled ? 'translate-x-5' : 'translate-x-0'} pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`} />
              </button>
            </div>

            {orgaConfig?.recruitment?.enabled && (
              <div className="space-y-4 mt-4 border-t border-border pt-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-foreground">
                    {t('admin.configuration.users.recruitment.descriptionTitle')}
                  </label>

                  {/* Language Selector */}
                  <div className="flex space-x-2">
                    {(['fr', 'en', 'es'] as const).map((lang) => (
                      <button
                        key={lang}
                        onClick={() => setRecruitmentLang(lang)}
                        className={`px-2 py-1 text-xs font-medium rounded transition-colors ${recruitmentLang === lang
                          ? 'bg-primary-10 text-primary-dark border border-primary-20'
                          : 'bg-surface text-foreground-secondary border border-border hover:bg-background-tertiary'
                          }`}
                      >
                        {lang.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="relative">
                  <textarea
                    value={descriptionDraft}
                    onChange={(e) => setDescriptionDraft(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm text-foreground bg-surface"
                    placeholder={t('admin.configuration.users.recruitment.descriptionPlaceholder', { lang: recruitmentLang.toUpperCase() })}
                  />
                </div>
                <div className="flex justify-between items-center mt-2">
                  <p className="text-xs text-foreground-secondary">
                    {t('admin.configuration.users.recruitment.descriptionHelp')}
                  </p>
                  <button
                    type="button"
                    onClick={handleDescriptionSave}
                    disabled={isSavingRecruitment}
                    className="flex items-center px-3 py-1.5 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
                    title={t('admin.configuration.users.recruitment.saveButton')}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {t('admin.configuration.users.recruitment.saveButton')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Formulaire d'ajout d'utilisateur */}
          {showAddUserForm && (
            <div className="mb-6 p-4 bg-background-tertiary rounded-lg">
              <h4 className="text-md font-medium text-foreground mb-3">{t('admin.configuration.users.addUser')}</h4>
              <form onSubmit={handleAddUser} className="space-y-4">
                <div>
                  <label htmlFor="userEmail" className="block text-sm font-medium text-foreground mb-2">
                    {t('admin.configuration.users.userEmailLabel')}
                  </label>
                  <input
                    id="userEmail"
                    type="email"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-foreground bg-surface"
                    placeholder={t('admin.configuration.users.emailPlaceholder')}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="userRole" className="block text-sm font-medium text-foreground mb-2">
                    {t('admin.configuration.users.role')}
                  </label>
                  <select
                    id="userRole"
                    value={newUserRole}
                    onChange={(e) => setNewUserRole(e.target.value as UserAffiliation['role'])}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-foreground bg-surface"
                  >
                    <option value="viewer">{t('admin.configuration.users.viewer')}</option>
                    <option value="member">{t('admin.configuration.users.member')}</option>
                    <option value="admin">{t('admin.configuration.users.admin')}</option>
                  </select>

                  {/* Affichage des permissions du rôle sélectionné */}
                  <div className="mt-3 p-3 bg-primary-10 border border-primary-20 rounded-lg">
                    <p className="text-sm font-medium text-foreground mb-2">
                      {t('admin.configuration.users.permissions.title')} ({getRoleLabel(newUserRole)})
                    </p>
                    <div className="space-y-2">
                      {Object.entries(getRolePermissions(newUserRole)).map(([permission, hasPermission]) => (
                        <div key={permission} className="flex items-center space-x-2 text-sm">
                          {hasPermission ? (
                            <CheckCircle className="w-4 h-4 text-success" />
                          ) : (
                            <XCircle className="w-4 h-4 text-foreground-secondary" />
                          )}
                          <span className={hasPermission ? 'text-foreground' : 'text-foreground-secondary'}>
                            {getPermissionLabel(permission as keyof UserAffiliation['permissions'])}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-success text-white rounded-lg hover:bg-success-dark transition-colors disabled:opacity-50"
                  >
                    {loading ? t('admin.configuration.users.adding') : t('admin.configuration.users.add')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddUserForm(false);
                      setNewUserEmail('');
                      setNewUserRole('member');
                    }}
                    className="px-4 py-2 bg-border-medium text-foreground-secondary rounded-lg hover:bg-border transition-colors"
                  >
                    {t('admin.configuration.users.cancel')}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Liste des utilisateurs */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-3">
              <h4 className="text-md font-medium text-foreground">
                {t('admin.configuration.users.organizationUsers')} ({organizationUsers.length})
              </h4>
              <Button
                variant="primary"
                size="md"
                onClick={() => setShowAddUserForm(true)}
                leftIcon={<Users className="w-4 h-4" />}
                className="w-full sm:w-auto"
              >
                {t('admin.configuration.users.addUser')}
              </Button>
            </div>

            {isLoadingUsers ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : organizationUsers.length === 0 ? (
              <div className="text-center py-8">
                <svg className="w-12 h-12 text-foreground-secondary mx-auto mb-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                </svg>
                <p className="text-foreground-secondary">{t('admin.configuration.users.noUsersInOrg')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {organizationUsers.map((affiliation) => (
                  <div
                    key={affiliation.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border border-border rounded-lg hover:bg-background-tertiary transition-colors gap-4"
                  >
                    <div className="flex items-center space-x-4 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-primary-10 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-primary" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                        </svg>
                      </div>

                      <div className="flex-1 min-w-0">
                        {(() => {
                          // ✅ Chercher les infos utilisateur par customerId
                          const userInfo = usersInfo.get(affiliation.customerId);
                          const userEmail = affiliation.email || userInfo?.email;
                          const fullName = userInfo ?
                            `${userInfo.firstName || ''} ${userInfo.lastName || ''}`.trim() : '';

                          return (
                            <>
                              <p className="font-medium text-foreground truncate">
                                {userEmail || affiliation.customerId}
                              </p>
                              {fullName && (
                                <p className="text-sm text-foreground-secondary truncate">
                                  {fullName}
                                </p>
                              )}
                              <p className="text-sm text-foreground-secondary flex flex-wrap gap-x-2">
                                {userInfo?.totalOrders && (
                                  <span>{t('admin.configuration.users.orders', { count: userInfo.totalOrders })}</span>
                                )}
                                <span>{t('admin.configuration.users.joinedOn')} {new Date(affiliation.joinedAt).toLocaleDateString('fr-FR')}</span>
                              </p>
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-3 w-full sm:w-auto">
                      {/* Rôle */}
                      <span className={`px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap ${getRoleBadgeColor(affiliation.role)}`}>
                        {getRoleLabel(affiliation.role)}
                      </span>

                      {/* Toggle notifications email SAV - Visible pour tous */}
                      <div className="flex items-center gap-2 px-2 py-1 bg-background-secondary rounded border border-border">
                        <Mail className="w-4 h-4 text-foreground-secondary" />
                        <button
                          onClick={() => handleToggleEmailNotifications(
                            affiliation.id,
                            affiliation.emailNotifications?.savMessages ?? true
                          )}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${(affiliation.emailNotifications?.savMessages ?? true)
                            ? 'bg-primary'
                            : 'bg-border'
                            }`}
                          title={(affiliation.emailNotifications?.savMessages ?? true) ? 'Notifications email activées' : 'Notifications email désactivées'}
                        >
                          <span
                            className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${(affiliation.emailNotifications?.savMessages ?? true) ? 'translate-x-5' : 'translate-x-1'
                              }`}
                          />
                        </button>
                      </div>

                      {/* Actions */}
                      {affiliation.role !== 'owner' && (
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto">
                          {/* Modifier le rôle */}
                          <select
                            value={affiliation.role}
                            onChange={(e) => handleUpdateRole(affiliation.id, e.target.value as UserAffiliation['role'])}
                            className="text-sm border border-border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary text-foreground bg-surface w-full sm:w-auto"
                          >
                            <option value="viewer">{t('admin.configuration.users.viewer')}</option>
                            <option value="member">{t('admin.configuration.users.member')}</option>
                            <option value="admin">{t('admin.configuration.users.admin')}</option>
                          </select>

                          {/* Affichage des permissions du rôle actuel */}
                          <div className="text-xs text-foreground-secondary sm:text-right">
                            <div className="flex items-center space-x-1">
                              {Object.entries(getRolePermissions(affiliation.role)).filter(([_, hasPermission]) => hasPermission).length > 0 ? (
                                <>
                                  <CheckCircle className="w-3 h-3 text-success" />
                                  <span>
                                    {Object.entries(getRolePermissions(affiliation.role)).filter(([_, hasPermission]) => hasPermission).length} {t('admin.configuration.users.permissions.active')}
                                  </span>
                                </>
                              ) : (
                                <span>{t('admin.configuration.users.permissions.viewOnly')}</span>
                              )}
                            </div>
                          </div>

                          {/* Supprimer */}
                          <button
                            onClick={() => handleRemoveUser(affiliation.id, affiliation.email || affiliation.customerId)}
                            className="text-error hover:text-error-dark transition-colors p-1 rounded hover:bg-error-10"
                            title={t('admin.configuration.users.removeUserTitle')}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default UserAffiliationManager;








